import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Close,
  CheckCircle,
  ExpandMore,
  MenuBook,
  Spellcheck,
  Style,
  Psychology,
  Lightbulb,
  TrendingUp
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const SuggestionPanel = ({ suggestions, onApplySuggestion, onClose, theme }) => {
  const [expandedSuggestion, setExpandedSuggestion] = useState(null);

  // Group suggestions by type
  const groupedSuggestions = suggestions.reduce((groups, suggestion) => {
    const type = suggestion.type || 'general';
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(suggestion);
    return groups;
  }, {});

  // Get icon for suggestion type
  const getTypeIcon = (type) => {
    switch (type) {
      case 'grammar':
        return <MenuBook />;
      case 'spelling':
        return <Spellcheck />;
      case 'style':
        return <Style />;
      case 'clarity':
        return <Lightbulb />;
      case 'punctuation':
        return <TrendingUp />;
      default:
        return <Psychology />;
    }
  };

  // Get color for suggestion type
  const getTypeColor = (type) => {
    switch (type) {
      case 'grammar':
        return '#ef4444';
      case 'spelling':
        return '#f59e0b';
      case 'style':
        return '#8b5cf6';
      case 'clarity':
        return '#10b981';
      case 'punctuation':
        return '#06b6d4';
      default:
        return theme.primary;
    }
  };

  // Get confidence color
  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return theme.success;
    if (confidence >= 0.6) return theme.warning;
    return theme.error;
  };

  return (
    <Paper
      elevation={3}
      sx={{
        backgroundColor: theme.surface,
        borderRadius: 2,
        height: '500px',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        borderBottom={`1px solid ${theme.textSecondary}30`}
      >
        <Typography variant="h6" color={theme.text}>
          <Psychology sx={{ mr: 1, verticalAlign: 'middle' }} />
          AI Suggestions
        </Typography>
        
        <Box display="flex" alignItems="center" gap={1}>
          <Badge badgeContent={suggestions.length} color="primary">
            <Chip
              label="Total"
              size="small"
              variant="outlined"
              sx={{ color: theme.text }}
            />
          </Badge>
          
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* Suggestions List */}
      <Box flex={1} overflow="auto" p={2}>
        {suggestions.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            color={theme.textSecondary}
          >
            <Psychology sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="body1" textAlign="center">
              No suggestions yet.
              <br />
              Analyze your text to get AI-powered improvements!
            </Typography>
          </Box>
        ) : (
          <AnimatePresence>
            {Object.entries(groupedSuggestions).map(([type, typeSuggestions]) => (
              <motion.div
                key={type}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <Box mb={3}>
                  {/* Type Header */}
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box
                      sx={{
                        color: getTypeColor(type),
                        mr: 1,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {getTypeIcon(type)}
                    </Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ color: theme.text, textTransform: 'capitalize' }}
                    >
                      {type} ({typeSuggestions.length})
                    </Typography>
                  </Box>

                  {/* Suggestions for this type */}
                  {typeSuggestions.map((suggestion, index) => (
                    <motion.div
                      key={`${type}-${index}`}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <Accordion
                        expanded={expandedSuggestion === `${type}-${index}`}
                        onChange={() => 
                          setExpandedSuggestion(
                            expandedSuggestion === `${type}-${index}` 
                              ? null 
                              : `${type}-${index}`
                          )
                        }
                        sx={{
                          mb: 1,
                          backgroundColor: theme.background,
                          '&:before': { display: 'none' },
                          borderRadius: 1,
                          border: `1px solid ${theme.textSecondary}20`
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMore sx={{ color: theme.text }} />}
                          sx={{
                            '& .MuiAccordionSummary-content': {
                              alignItems: 'center'
                            }
                          }}
                        >
                          <Box flex={1}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                              <Typography
                                variant="body2"
                                sx={{
                                  color: theme.text,
                                  fontWeight: 500,
                                  maxWidth: '70%'
                                }}
                              >
                                {suggestion.original.length > 50
                                  ? `${suggestion.original.substring(0, 50)}...`
                                  : suggestion.original}
                              </Typography>
                              
                              {suggestion.confidence && (
                                <Chip
                                  label={`${Math.round(suggestion.confidence * 100)}%`}
                                  size="small"
                                  sx={{
                                    backgroundColor: getConfidenceColor(suggestion.confidence),
                                    color: 'white',
                                    fontSize: '0.7rem'
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </AccordionSummary>
                        
                        <AccordionDetails>
                          <Box>
                            {/* Original Text */}
                            <Box mb={2}>
                              <Typography variant="caption" color={theme.textSecondary}>
                                Original:
                              </Typography>
                              <Paper
                                sx={{
                                  p: 1,
                                  mt: 0.5,
                                  backgroundColor: `${theme.error}15`,
                                  border: `1px solid ${theme.error}30`
                                }}
                              >
                                <Typography variant="body2" color={theme.text}>
                                  {suggestion.original}
                                </Typography>
                              </Paper>
                            </Box>

                            {/* Suggested Text */}
                            <Box mb={2}>
                              <Typography variant="caption" color={theme.textSecondary}>
                                Suggestion:
                              </Typography>
                              <Paper
                                sx={{
                                  p: 1,
                                  mt: 0.5,
                                  backgroundColor: `${theme.success}15`,
                                  border: `1px solid ${theme.success}30`
                                }}
                              >
                                <Typography variant="body2" color={theme.text}>
                                  {suggestion.suggestion}
                                </Typography>
                              </Paper>
                            </Box>

                            {/* Explanation */}
                            {suggestion.explanation && (
                              <Box mb={2}>
                                <Typography variant="caption" color={theme.textSecondary}>
                                  Explanation:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color={theme.textSecondary}
                                  sx={{ mt: 0.5, fontStyle: 'italic' }}
                                >
                                  {suggestion.explanation}
                                </Typography>
                              </Box>
                            )}

                            {/* Confidence Bar */}
                            {suggestion.confidence && (
                              <Box mb={2}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                  <Typography variant="caption" color={theme.textSecondary}>
                                    Confidence:
                                  </Typography>
                                  <Typography variant="caption" color={theme.textSecondary}>
                                    {Math.round(suggestion.confidence * 100)}%
                                  </Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={suggestion.confidence * 100}
                                  sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: `${theme.textSecondary}20`,
                                    '& .MuiLinearProgress-bar': {
                                      backgroundColor: getConfidenceColor(suggestion.confidence),
                                      borderRadius: 3
                                    }
                                  }}
                                />
                              </Box>
                            )}

                            {/* Apply Button */}
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<CheckCircle />}
                              onClick={() => onApplySuggestion(suggestion)}
                              sx={{
                                backgroundColor: theme.success,
                                '&:hover': {
                                  backgroundColor: `${theme.success}dd`
                                }
                              }}
                            >
                              Apply Suggestion
                            </Button>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </motion.div>
                  ))}
                </Box>
                
                {Object.keys(groupedSuggestions).indexOf(type) < Object.keys(groupedSuggestions).length - 1 && (
                  <Divider sx={{ my: 2, backgroundColor: `${theme.textSecondary}30` }} />
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </Box>

      {/* Footer */}
      {suggestions.length > 0 && (
        <Box
          p={2}
          borderTop={`1px solid ${theme.textSecondary}30`}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="caption" color={theme.textSecondary}>
            Click on suggestions to expand and apply them
          </Typography>
          
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              suggestions.forEach(suggestion => onApplySuggestion(suggestion));
            }}
            sx={{ color: theme.text, borderColor: theme.textSecondary }}
          >
            Apply All
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default SuggestionPanel;
